import { useMutation } from '@apollo/client';

import type {
  CreateSearchAlertByQueryMutation,
  CreateSearchAlertByQueryMutationVariables,
} from '@xing-com/crate-jobs-graphql';
import { CreateSearchAlertByQueryDocument } from '@xing-com/crate-jobs-graphql';
import { useViewerData } from '@xing-com/hub';

import { addAlertByQueryToCachedList } from './apollo-helpers';
import type {
  SearchAlertHookParams,
  UseCreateSearchAlertByQuery,
} from './types';
import { useShowSearchAlertErrorBanner } from './use-show-search-alert-error-banner';

const createOptimisticUpdate = (
  variables: CreateSearchAlertByQueryMutationVariables
): CreateSearchAlertByQueryMutation => ({
  __typename: 'Mutation',
  createSearchAlertByQueryNew: {
    __typename: 'SearchAlertCreationSuccess',
    alert: {
      __typename: 'SearchAlert',
      id: 'optimistic-response-id',
      globalId: 'optimistic-response-global-id',
      queryId: 'optimistic-response-query-id',
      query: { body: variables },
      name: 'optimistic-response-name',
      newJobCount: null,
      createdAt: new Date().toString(),
      visitedAt: new Date().toString(),
    },
  },
});

export const useCreateSearchAlertByQuery = (
  params?: SearchAlertHookParams
): UseCreateSearchAlertByQuery => {
  const onCreateError = useShowSearchAlertErrorBanner(
    'CREATE',
    params?.nwtPage
  );
  const onDuplicatedError = useShowSearchAlertErrorBanner(
    'DUPLICATED',
    params?.nwtPage
  );

  const DEFAULT_PARAMS = {
    disableOptimisticUpdate: false,
    onError: (type?: string): void => {
      if (type === 'DUPLICATED') {
        onDuplicatedError();
      } else {
        onCreateError();
      }
    },
  };

  const { disableOptimisticUpdate, onError } = {
    ...DEFAULT_PARAMS,
    ...params,
  };

  const [createSearchAlertByQueryX1, { loading }] = useMutation(
    CreateSearchAlertByQueryDocument
  );
  const userId = useViewerData().data?.user?.id;
  return [
    ({ variables, onCompleted }) => {
      if (!variables) {
        return;
      }

      const { keywords, location, filter, origin } = variables;
      createSearchAlertByQueryX1({
        variables: { keywords, location, filter, origin },
        update: addAlertByQueryToCachedList(userId),
        optimisticResponse: disableOptimisticUpdate
          ? undefined
          : createOptimisticUpdate(variables),
        onCompleted: (response) => {
          if (
            response.createSearchAlertByQueryNew?.__typename ===
            'SearchAlertErrorResponse'
          ) {
            const { code } = response.createSearchAlertByQueryNew ?? {};
            onError(code === 'ALERT_EXISTS_ALREADY' ? 'DUPLICATED' : 'CREATE');
          }

          onCompleted?.(response);
        },
        onError: () => onError(),
      });
    },
    { isLoading: loading },
  ];
};
